import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CustomerMessageResponseBody,
  CustomerMessageSecondaryRecordResponseBody,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {entityResourceResolver} from '../../../utils/entityResourceResolver';
import {MESSAGE_CONTEXT_RESOURCES} from '../constants/MESSAGE_CONTEXT_RESOURCES';
import {MessageContextHostRecord} from '../types/MessageContextHostRecord';
import {MessageContextLink} from '../types/MessageContextLink';
import {MessageContextResource} from '../types/MessageContextResource';

interface MessageContextRecord {
  recordId: string | Nullish;
  resourceId: string | Nullish;
}

interface LinkableContextRecord {
  recordId: string;
  resourceId: MessageContextResource;
}

/**
 * Secondary records double as the `additionalParams` of `entityResourceResolver` — a service order
 * detail, for instance, is only reachable through the service case it belongs to.
 */
const ADDITIONAL_PARAM_BY_RESOURCE: Record<string, string | undefined> = {
  [EntityResourceIds.serviceCase]: 'serviceCaseId',
};

const getAdditionalParams = (
  secondaryRecords: CustomerMessageSecondaryRecordResponseBody[]
): Record<string, string> =>
  secondaryRecords.reduce<Record<string, string>>((params, secondaryRecord) => {
    const paramName = ADDITIONAL_PARAM_BY_RESOURCE[secondaryRecord.resourceId];

    if (isNil(paramName)) {
      return params;
    }

    return {...params, [paramName]: secondaryRecord.recordId};
  }, {});

export const isMessageContextResource = (
  value: string | Nullish
): value is MessageContextResource =>
  MESSAGE_CONTEXT_RESOURCES.some((resource) => resource === value);

const isLinkableContextRecord = (record: MessageContextRecord): record is LinkableContextRecord =>
  isNotNilOrEmpty(record.recordId) && isMessageContextResource(record.resourceId);

const isResolvable = (record: LinkableContextRecord, additionalParams: Record<string, string>) =>
  record.resourceId !== EntityResourceIds.serviceOrder ||
  isNotNilOrEmpty(additionalParams.serviceCaseId);

const isHostRecord = (record: LinkableContextRecord, hostRecord: MessageContextHostRecord) =>
  record.recordId === hostRecord.recordId && record.resourceId === hostRecord.resourceId;

type ResolvedContextLink = Omit<MessageContextLink, 'path'> & {path: string | undefined};

/**
 * `entityResourceResolver` returns no path for a resource without a detail route. The allowlist
 * above already excludes those, so this is the type-level proof of a guarantee the filters make.
 */
const isMessageContextLink = (link: ResolvedContextLink): link is MessageContextLink =>
  isNotNil(link.path);

/**
 * @about Resolves every record a customer message is linked to into a detail-page path.
 *
 * The primary record comes first, secondary records follow in response order. Records are dropped
 * when they point at the host record (that is the page the thread is already on), when the
 * resource has no detail page, or when a service order has no service case in either its secondary
 * records or the current host and there is therefore no URL to build.
 *
 * @example
 * getMessageContextLinks(businessCaseMessage, {recordId: 'c-1', resourceId: EntityResourceIds.customer})
 * // => [{resourceId: 'BUSINESS_CASE', recordId: 'bc-1', path: '/business-case/bc-1/overview'}]
 */
export function getMessageContextLinks(
  message: CustomerMessageResponseBody,
  hostRecord: MessageContextHostRecord,
  includeHostRecord = false
): MessageContextLink[] {
  const additionalParams = {
    ...(hostRecord.resourceId === EntityResourceIds.serviceCase
      ? {serviceCaseId: hostRecord.recordId}
      : {}),
    ...getAdditionalParams(message.secondaryRecords),
  };

  const records: MessageContextRecord[] = [
    {recordId: message.recordId, resourceId: message.resourceId},
    ...message.secondaryRecords,
  ];

  return records
    .filter(isLinkableContextRecord)
    .filter(
      (record) =>
        isResolvable(record, additionalParams) &&
        (includeHostRecord || !isHostRecord(record, hostRecord))
    )
    .map((record) => ({
      resourceId: record.resourceId,
      recordId: record.recordId,
      path: entityResourceResolver(record.resourceId, record.recordId, additionalParams),
    }))
    .filter(isMessageContextLink);
}
